import { useState } from 'react';
import { Modal } from './parts';
import { CardIcon } from './Icon';
import { CardDetail } from './CardDetail';
import { categoryDefs, deckCount } from '../game/cardMeta';
import { cardArt } from '../game/art';
import { useT } from '../i18n';
import type { CardKind } from '../game/types';

const DECK_TOTAL = Object.values(deckCount).reduce((a, b) => a + b, 0);

/** Reference of every card, grouped by category. Reachable from Home and Table. */
export function CardCodex({ onClose }: { onClose: () => void }) {
  const t = useT();
  // Tapping a row opens the big parallax card view (kind + its category colour).
  const [open, setOpen] = useState<{ kind: CardKind; color: string } | null>(null);

  return (
    <Modal onClose={onClose}>
      <div className="col" style={{ gap: 18 }}>
        <div className="row between">
          <div className="col" style={{ gap: 2 }}>
            <h2 className="h2">{t.codex.title}</h2>
            <span className="mono-dim" style={{ fontSize: 12 }}>{t.codex.total(DECK_TOTAL)}</span>
          </div>
          <button className="link" onClick={onClose}>{t.codex.close}</button>
        </div>

        {categoryDefs.map((cat) => (
          <div key={cat.key} className="col" style={{ gap: 8 }}>
            <div
              className="eyebrow"
              style={{ color: cat.color, borderBottom: `1px solid ${cat.color}`, paddingBottom: 6 }}
            >
              {t.categories[cat.key]}
            </div>
            {cat.kinds.map((k) => (
              <button
                key={k}
                className="codex-card-row"
                onClick={() => setOpen({ kind: k, color: cat.color })}
              >
                <span
                  className="codex-card-art"
                  style={{ color: cat.color, backgroundImage: `url(${cardArt(k)})` }}
                >
                  {/* Corner badge, not a centred overlay — keeps the art readable. */}
                  <span className="codex-card-mark"><CardIcon kind={k} size={13} /></span>
                </span>
                <div className="col" style={{ gap: 2, flex: 1, minWidth: 0 }}>
                  <div className="row between" style={{ gap: 8 }}>
                    <span style={{ fontWeight: 800, color: cat.color }}>{t.card.name[k]}</span>
                    <span className="codex-count mono-dim" title={t.codex.inDeck}>×{deckCount[k]}</span>
                  </div>
                  <span className="mono-dim codex-card-desc">{t.card.desc[k]}</span>
                </div>
              </button>
            ))}
          </div>
        ))}
      </div>

      {open && (
        <CardDetail kind={open.kind} color={open.color} onClose={() => setOpen(null)} />
      )}
    </Modal>
  );
}
